import React, { useState } from 'react';
import axios from 'axios';

interface FormData {
  senderName: string;
  senderPosition: string;
  letterDescription: string;
  recipientFio: string;
  recipientPosition: string;
  recipientOrganization: string;
}

const EmailForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    senderName: '',
    senderPosition: '',
    letterDescription: '',
    recipientFio: '',
    recipientPosition: '',
    recipientOrganization: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.senderName || !formData.letterDescription) {
      setAlert({ type: 'error', message: 'Пожалуйста, заполните все обязательные поля' });
      return false;
    }

    if (formData.letterDescription.length < 10) {
      setAlert({ type: 'error', message: 'Пожалуйста, опишите письмо более подробно (минимум 10 символов)' });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setAlert(null);

    try {
      // Определяем URL API: используем переменную окружения или определяем автоматически
      let apiUrl = process.env.REACT_APP_API_URL;
      
      // Если переменная не задана или пустая строка, определяем автоматически
      if (!apiUrl || apiUrl.trim() === '') {
        // Автоматически определяем URL на основе текущего домена
        const { protocol, hostname } = window.location;
        
        // Хардкод для test4.m-docs.com - используем относительный путь (через nginx)
        if (hostname === 'test4.m-docs.com') {
          apiUrl = ''; // Относительный путь - nginx проксирует на localhost:5005
        } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
          // Для localhost используем прямой порт
          apiUrl = 'http://localhost:5005';
        } else {
          // Для остальных HTTP доменов используем HTTP с портом 5005
          apiUrl = `http://${hostname}:5005`;
        }
      }
      
      // Если apiUrl пустой, используем относительный путь (сервер на том же домене)
      const apiEndpoint = apiUrl ? `${apiUrl}/api/send-email` : '/api/send-email';
      console.log('🌐 Используется API URL:', apiUrl || 'относительный путь (текущий домен)');
      console.log('🌐 Полный endpoint:', apiEndpoint);
      const response = await axios.post(apiEndpoint, {
        ...formData,
        useGigaChat: true // Всегда используем GigaChat
      }, {
        responseType: 'blob' // Важно: указываем, что ожидаем бинарные данные
      });
      
      // Создаем ссылку для скачивания файла
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Получаем имя файла из заголовка Content-Disposition
      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'Письмо.docx';
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = decodeURIComponent(fileNameMatch[1].replace(/['"]/g, ''));
        }
      }
      
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setAlert({ type: 'success', message: 'Word документ успешно сгенерирован и скачан!' });
      setFormData({ 
        senderName: '', 
        senderPosition: '', 
        letterDescription: '',
        recipientFio: '',
        recipientPosition: '',
        recipientOrganization: ''
      });
    } catch (error: any) {
      console.error('Ошибка генерации документа:', error);
      
      // Если ошибка в формате JSON
      if (error.response?.data && error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          setAlert({ 
            type: 'error', 
            message: errorData.error || 'Произошла ошибка при генерации документа' 
          });
        } catch {
          setAlert({ 
            type: 'error', 
            message: 'Ошибка соединения с сервером' 
          });
        }
      } else {
        setAlert({ 
          type: 'error', 
          message: error.response?.data?.error || 'Ошибка соединения с сервером' 
        });
      }
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="form-container">
      {alert && (
        <div className={`alert alert-${alert.type}`}>
          {alert.message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="senderName">
            Ваше ФИО
          </label>
          <input
            type="text"
            id="senderName"
            name="senderName"
            value={formData.senderName}
            onChange={handleInputChange}
            placeholder="Иванов Иван Иванович"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="senderPosition">
            Ваша должность
          </label>
          <input
            type="text"
            id="senderPosition"
            name="senderPosition"
            value={formData.senderPosition}
            onChange={handleInputChange}
            placeholder="Управляющий директор"
          />
        </div>

        <div className="form-group">
          <label htmlFor="recipientFio">
            ФИО получателя
          </label>
          <input
            type="text"
            id="recipientFio"
            name="recipientFio"
            value={formData.recipientFio}
            onChange={handleInputChange}
            placeholder="Иванов Иван Иванович"
          />
        </div>

        <div className="form-group">
          <label htmlFor="recipientPosition">
            Должность получателя
          </label>
          <input
            type="text"
            id="recipientPosition"
            name="recipientPosition"
            value={formData.recipientPosition}
            onChange={handleInputChange}
            placeholder="Директор"
          />
        </div>

        <div className="form-group">
          <label htmlFor="recipientOrganization">
            Организация получателя
          </label>
          <input
            type="text"
            id="recipientOrganization"
            name="recipientOrganization"
            value={formData.recipientOrganization}
            onChange={handleInputChange}
            placeholder="ООО «Компания»"
          />
        </div>

        <div className="form-group">
          <label htmlFor="letterDescription">
            Описание письма *
          </label>
          <textarea
            id="letterDescription"
            name="letterDescription"
            value={formData.letterDescription}
            onChange={handleInputChange}
            placeholder="Опишите, что вы хотите написать в письме. Например: 'Приглашаю на встречу по поводу сотрудничества', 'Прошу предоставить информацию о ваших услугах', 'Благодарю за быстрый ответ' и т.д."
            rows={4}
            required
          />
        </div>

        <div className="button-group">
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary"
          >
            {isLoading ? (
              <div className="loading">
                <div className="spinner"></div>
                Генерация...
              </div>
            ) : (
              'Сформировать письмо'
            )}
          </button>
        </div>
      </form>

    </div>
  );
};

export default EmailForm;